import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, ArrowLeft } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { vendorAdvanceService } from '@/services/vendorAdvanceService'
import { billsService } from '@/services/billsService'
import { VendorAdvanceVm, BillVendor, Account } from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const VendorAdvancesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [advance, setAdvance] = useState<VendorAdvanceVm>({
    id: 0,
    vendorId: 0,
    creditAccount: 0,
    paymentType: '',
    amount: 0,
    date: calendarToday(),
    refNo: '',
    note: '',
    branchId: branchId || 0,
    ipAddress: '',
  })

  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [, setSelectedVendor] = useState<BillVendor | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('vendorAdvances.bank') },
    { value: 'Cash', label: t('vendorAdvances.cash') },
  ]

  // Load vendors and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [vendorsData, accountsData] = await Promise.all([
          billsService.getAllVendors(branchId),
          vendorAdvanceService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        await showError('Error', error.response?.data?.message || t('vendorAdvances.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load advance data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id || vendors.length === 0) return

    const loadAdvance = async () => {
      try {
        setLoading(true)
        const advanceId = Number(id)
        const advanceData = await vendorAdvanceService.getAdvanceById(branchId, advanceId)

        // Find vendor
        const vendor = vendors.find(v => v.vendorId === advanceData.vendorId)
        if (vendor) {
          setSelectedVendor(vendor)
        }

        setAdvance({
          id: advanceData.id || 0,
          vendorId: advanceData.vendorId || 0,
          creditAccount: advanceData.creditAccount || 0,
          paymentType: advanceData.paymentType || '',
          amount: advanceData.amount || 0,
          date: calendarFromApi(advanceData.date) || calendarToday(),
          refNo: advanceData.refNo || '',
          note: advanceData.note || '',
          branchId: branchId,
          ipAddress: advanceData.ipAddress || '',
        })
      } catch (error: any) {
        await showError('Error', error.message || t('vendorAdvances.failedToLoadAdvance'))
      } finally {
        setLoading(false)
      }
    }

    loadAdvance()
  }, [isEditMode, branchId, id, vendors.length])

  // Handle vendor selection
  const handleVendorChange = (vendorId: string) => {
    const vendor = vendors.find(v => v.vendorId === Number(vendorId))
    if (vendor && vendor.vendorId) {
      setSelectedVendor(vendor)
      setAdvance(prev => ({
        ...prev,
        vendorId: vendor.vendorId!,
      }))
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('vendorAdvances.pleaseSelectBranch'))
      return
    }

    if (!advance.vendorId || advance.vendorId === 0) {
      await showValidationError(t('vendorAdvances.pleaseSelectVendor'))
      return
    }

    if (!advance.creditAccount || advance.creditAccount === 0) {
      await showValidationError(t('vendorAdvances.pleaseSelectDestinationAccount'))
      return
    }

    if (!advance.paymentType) {
      await showValidationError(t('vendorAdvances.pleaseSelectPaymentType'))
      return
    }

    if (!advance.amount || advance.amount <= 0) {
      await showValidationError(t('vendorAdvances.pleaseEnterValidAmount'))
      return
    }

    if (!advance.date) {
      await showValidationError(t('vendorAdvances.pleaseSelectDate'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? t('vendorAdvances.updatingAdvance') : t('vendorAdvances.savingAdvance'),
        t('vendorAdvances.pleaseWait')
      )

      // Get user IP address
      const ipAddress = await getUserIPAddress()

      const dateISO = calendarToApi(advance.date)

      const advanceToSave: VendorAdvanceVm = {
        id: advance.id || 0,
        vendorId: advance.vendorId,
        creditAccount: advance.creditAccount,
        paymentType: advance.paymentType,
        amount: advance.amount,
        date: dateISO,
        refNo: advance.refNo || '',
        note: advance.note || '',
        branchId: branchId,
        ipAddress: ipAddress,
      }

      if (isEditMode) {
        await vendorAdvanceService.updateAdvance(advanceToSave)
      } else {
        await vendorAdvanceService.insertAdvance(advanceToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('vendorAdvances.advanceUpdatedSuccess') : t('vendorAdvances.advanceCreatedSuccess')
      )

      navigate('/purchases/vendor-advances')
    } catch (error: any) {
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const vendorOptions: SearchableSelectOption[] = vendors.map((vendor) => ({
    value: String(vendor.vendorId || 0),
    label: vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('navigation.vendorAdvances')} description={isEditMode ? t('vendorAdvances.editAdvance') : t('vendorAdvances.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.vendorAdvances')}
        description={isEditMode ? t('vendorAdvances.editAdvance') : t('vendorAdvances.createNew')}
        action={{
          label: t('vendorAdvances.return'),
          onClick: () => navigate('/purchases/vendor-advances'),
          icon: ArrowLeft,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Vendor Section */}
            <div className="space-y-2">
              <Label>
                {t('vendorAdvances.vendor')} <span className="text-destructive">*</span>
              </Label>
              <div className="max-w-[400px]">
                <SearchableSelect
                  options={vendorOptions}
                  value={advance.vendorId ? String(advance.vendorId) : ''}
                  onValueChange={handleVendorChange}
                  placeholder={t('vendorAdvances.selectVendor')}
                  searchPlaceholder={t('vendorAdvances.searchVendors')}
                  emptyMessage={t('vendorAdvances.noVendorsFound')}
                />
              </div>
            </div>

            {/* Advance Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('vendorAdvances.advanceInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('vendorAdvances.date')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={advance.date}
                      onChange={(e) => setAdvance(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('vendorAdvances.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={advance.paymentType}
                      onValueChange={(value) => setAdvance(prev => ({ ...prev, paymentType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('vendorAdvances.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('vendorAdvances.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={advance.refNo || ''}
                      onChange={(e) => setAdvance(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('vendorAdvances.enterReferenceNumber')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('vendorAdvances.destinationAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={advance.creditAccount ? String(advance.creditAccount) : ''}
                      onValueChange={(value) => setAdvance(prev => ({ ...prev, creditAccount: Number(value) }))}
                      placeholder={t('vendorAdvances.selectAccount')}
                      searchPlaceholder={t('vendorAdvances.searchAccounts')}
                      emptyMessage={t('vendorAdvances.noAccountsFound')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('vendorAdvances.amount')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={advance.amount || ''}
                      onChange={(e) => setAdvance(prev => ({ ...prev, amount: Number(e.target.value) || 0 }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('vendorAdvances.note')}</Label>
              <Textarea
                value={advance.note || ''}
                onChange={(e) => setAdvance(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('vendorAdvances.enterNotes')}
                rows={4}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/purchases/vendor-advances')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('vendorAdvances.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('vendorAdvances.updateAdvance') : t('vendorAdvances.saveAdvance')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

